import { DataGrid } from '@mui/x-data-grid'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import * as React from 'react'
import { useQuery } from '@apollo/client'
import { getAllSections } from '../../graphql/queries'
import ProgressBar from '../circularProgress/circularProgress'
import { Box, Button, Chip } from '@mui/material'
import { useState, useEffect } from 'react'
import { useDataRefresh } from '../../hooks/useDataRefresh'

const columns = [
  {
    field: 'tableIndex',
    headerName: '#',
    flex: 0.2,
    minWidth: 150,
    filterable: false,
    sortable: false,
  },
  { field: 'name', headerName: 'Name', flex: 2 },
  { field: 'designType', headerName: 'Design Type', flex: 1.5 },
  { field: 'rowNumber', headerName: 'Row Number', flex: 1 },
  {
    field: 'isPublic',
    headerName: 'Public',
    flex: 1,
    renderCell: params => (
      <Chip
        label={params.value ? 'Yes' : 'No'}
        color={params.value ? 'success' : 'default'}
        size="small"
      />
    ),
  },
  {
    field: 'styles',
    headerName: 'Styles',
    flex: 2,
    renderCell: params => {
      const styles = params.value || []
      if (styles.length === 0) {
        return (
          <Chip
            label="No styles"
            color="default"
            size="small"
            variant="outlined"
          />
        )
      }
      return (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, maxWidth: '100%' }}>
          <Chip
            label={`${styles.length} styles`}
            color="secondary"
            size="small"
          />
          {styles.slice(0, 2).map((style) => (
            <Chip
              key={style.id}
              label={style.name}
              color="primary"
              size="small"
              variant="outlined"
            />
          ))}
          {styles.length > 2 && (
            <Chip
              label={`+${styles.length - 2}`}
              color="default"
              size="small"
              variant="outlined"
            />
          )}
        </Box>
      )
    },
  },
  {
    field: 'tools',
    headerName: 'Prompts Count',
    flex: 1,
    renderCell: params => params.value?.length || 0,
  },
]

export default function SectionsTable() {
  const [searchParams, setSearchParams] = useSearchParams()
  const initialPage = parseInt(searchParams.get('page')) || 0
  const initialPageSize = parseInt(searchParams.get('pageSize')) || 50
  const [page, setPage] = useState(initialPage)
  const [pageSize, setPageSize] = useState(initialPageSize)

  const navigate = useNavigate()

  // URL query param güncelleme
  useEffect(() => {
    setSearchParams({
      page: page.toString(),
      pageSize: pageSize.toString(),
    })
  }, [page, pageSize, setSearchParams])

  const { data, loading, error, refetch } = useQuery(getAllSections, {
    fetchPolicy: 'cache-and-network',
  })

  // Enable automatic data refresh when returning from detail pages
  useDataRefresh(refetch)

  if (loading) return <ProgressBar />
  if (error) return <p>Error: {error.message}</p>

  const rowsData = data?.getAllSections || []
  const rowsWithIndex = rowsData.map((row, index) => ({
    ...row,
    tableIndex: page * pageSize + index + 1,
  }))

  const handleRowClick = params => {
    navigate(`/sections/${params.id}`)
  }

  const handlePaginationModelChange = newModel => {
    setPage(newModel.page)
    setPageSize(newModel.pageSize)
  }

  return (
    <Box style={{ width: '100%' }}>
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', py: 2 }}>
        <Link to="/sections/create">
          <Button
            sx={{ color: '#212529', '&:hover': { backgroundColor: '#212529', color: '#fff' } }}
          >
            Create
          </Button>
        </Link>
      </Box>
      <DataGrid
        rows={rowsWithIndex}
        columns={columns}
        rowHeight={70}
        disableColumnMenu
        page={page}
        onPageChange={newPage => setPage(newPage)}
        pageSize={pageSize}
        onPageSizeChange={newSize => {
          setPageSize(newSize)
          setPage(0)
        }}
        rowCount={rowsData.length}
        onRowClick={handleRowClick}
        rowsPerPageOptions={[25, 50, 100]}
        loading={loading}
        pagination
        paginationModel={{ page, pageSize }}
        onPaginationModelChange={handlePaginationModelChange}
        pageSizeOptions={[25, 50, 100]}
        sx={{
          height: 680,
          '& .MuiDataGrid-virtualScroller': {
            msOverflowY: 'auto',
          },
          fontSize: '16px',
          '& .MuiDataGrid-columnHeaderTitle': {
            fontWeight: 'bold',
          },
          '& .MuiDataGrid-row:not(.MuiDataGrid-row--dynamicHeight) > .MuiDataGrid-cell': {
            whiteSpace: 'break-spaces',
          },
          '.MuiFormControl-root': {
            margin: '0px 0px 10px 0px',
            width: '100px',
          },
          '.MuiOutlinedInput-root': {
            width: '150px',
          },
          '.MuiToolbar-regular': {
            padding: 0,
          },
        }}
      />
    </Box>
  )
} 